// Lazily loaded popup of `Popover` (Base UI). Open state, trigger and layer live in Popover.tsx.
import { Popover as BasePopover } from '@base-ui/react/popover';
import {
  type CSSProperties,
  type MouseEvent,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { SheetGrabber, sideAlign } from '../layers/base';
import { closeReasonOf } from '../layers/base';
import type { LayerCloseReason } from '../layers/types';
import { type LayerHandle, LayerParent } from '../layers/useLayer';
import { cx } from './cx';
import type { PopoverProps } from './Popover';

const CLOSE_ON_SELECT =
  '[role="menuitem"],[role="menuitemcheckbox"],[role="menuitemradio"],[role="option"],[data-close-on-select]';

const TABBABLE =
  'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

function scrollParent(el: Element | null): Element | null {
  for (let node = el?.parentElement ?? null; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(`${style.overflowY}${style.overflowX}`)) return node;
  }
  return null;
}

type Details = { reason: string; event: Event; cancel: () => void; allowPropagation: () => void };

/**
 * Whether a closing popup may send focus back to its trigger. Base UI returns focus when the popup
 * unmounts, after its exit animation (about 150 ms). Whoever pressed or focused something else in
 * the meantime (the next picker, a field) keeps that focus: returning it would drop what they type
 * there and make Enter reopen this popup. Shared by popovers and menus.
 */
export function useFocusReturnGuard(
  open: boolean,
  popupRef: RefObject<HTMLElement | null>,
  anchor: HTMLElement,
): () => boolean {
  const movedOn = useRef(false);
  const closing = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) {
      movedOn.current = false;
      closing.current = null;
      return;
    }
    closing.current = popupRef.current;
    const elsewhere = (e: Event) => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (closing.current?.contains(target) || anchor.contains(target)) return;
      movedOn.current = true;
    };
    document.addEventListener('pointerdown', elsewhere, true);
    document.addEventListener('focusin', elsewhere, true);
    return () => {
      document.removeEventListener('pointerdown', elsewhere, true);
      document.removeEventListener('focusin', elsewhere, true);
    };
  }, [open, popupRef, anchor]);
  return useCallback(() => {
    if (movedOn.current) return false;
    const active = document.activeElement;
    if (!active || active === document.body || active === anchor) return true;
    const popup = closing.current ?? popupRef.current;
    // Focus left the popup for another control (Tab, a click on a field): leave it there.
    return !popup || popup.contains(active);
  }, [popupRef, anchor]);
}

/**
 * Base UI dismiss → layer decision. Presses on the trigger are not "outside" (the trigger toggles),
 * the stack decides the rest (top layer, dirty guard).
 */
export function dismissWithTrigger(
  layer: LayerHandle,
  anchor: HTMLElement,
  details: Details,
): LayerCloseReason | null {
  const target =
    details.reason === 'focus-out' ? (details.event as FocusEvent).relatedTarget : details.event?.target;
  if (
    (details.reason === 'outside-press' || details.reason === 'focus-out') &&
    target instanceof Node &&
    anchor.contains(target)
  ) {
    details.cancel();
    return null;
  }
  const reason = closeReasonOf(details.reason);
  const verdict = layer.dismiss(reason, details.event);
  if (verdict === 'close') return reason;
  details.cancel();
  if (verdict === 'ignore') details.allowPropagation();
  return null;
}

export default function PopoverPopup({
  children,
  placement = 'bottom-start',
  panel,
  className,
  width,
  label,
  initialFocus,
  manageFocus = true,
  closeOnSelect,
  boundary = 'viewport',
  closeOnAnchorHidden = true,
  positionAnchor,
  open,
  popupId,
  anchor,
  layer,
  compact,
  lastReason,
  onRequestOpenChange,
  onExited,
}: PopoverProps & {
  open: boolean;
  popupId: string;
  anchor: HTMLElement;
  layer: LayerHandle;
  compact: boolean;
  lastReason: RefObject<LayerCloseReason | null>;
  onRequestOpenChange: (open: boolean, reason?: LayerCloseReason) => void;
  onExited: () => void;
}) {
  const popupRef = useRef<HTMLDivElement | null>(null);
  const mayReturnFocus = useFocusReturnGuard(open, popupRef, anchor);
  const [positioner, setPositioner] = useState<HTMLDivElement | null>(null);

  // Anchored popovers close when the anchor scrolls out of view (inline editors commit then).
  useEffect(() => {
    if (!open || compact || !closeOnAnchorHidden || !positioner) return;
    const visible = () => {
      const rect = anchor.getBoundingClientRect();
      return rect.width > 0 || rect.height > 0;
    };
    // Only a transition from visible to hidden counts (a never-laid-out anchor is not "scrolled out").
    let seenVisible = !positioner.hasAttribute('data-anchor-hidden') && visible();
    const observer = new MutationObserver(() => {
      if (!positioner.hasAttribute('data-anchor-hidden')) {
        seenVisible = seenVisible || visible();
        return;
      }
      if (seenVisible) void layer.requestClose('anchor-hidden');
    });
    observer.observe(positioner, { attributes: true, attributeFilter: ['data-anchor-hidden'] });
    return () => observer.disconnect();
  }, [open, compact, closeOnAnchorHidden, positioner, layer, anchor]);

  const close = () => onRequestOpenChange(false, 'programmatic');
  const { side, align } = sideAlign(placement);
  const style: CSSProperties = {};
  if (typeof width === 'number') style.width = width;
  else if (width === 'trigger') style.width = 'var(--anchor-width)';

  const focusOnOpen =
    !manageFocus || initialFocus === undefined
      ? manageFocus
      : () => {
          const popup = popupRef.current;
          if (!popup) return true;
          if (initialFocus < 0) return popup;
          // Document order (some engines group selector-list matches) without Base UI's focus guards.
          const tabbables = Array.from(popup.querySelectorAll<HTMLElement>(TABBABLE))
            .filter((el) => !el.hasAttribute('data-base-ui-focus-guard') && el.tabIndex >= 0)
            .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
          return tabbables[initialFocus] ?? true;
        };

  return (
    <BasePopover.Root
      open={open}
      modal={false}
      onOpenChange={(next, details) => {
        if (next) return onRequestOpenChange(true);
        const reason = dismissWithTrigger(layer, anchor, details);
        if (reason) onRequestOpenChange(false, reason);
      }}
      onOpenChangeComplete={(isOpen) => {
        if (!isOpen) onExited();
      }}
    >
      <BasePopover.Portal container={layer.container} className="dr-layer" {...layer.portalProps}>
        {compact ? <BasePopover.Backdrop className="scrim sheet-scrim" /> : null}
        <BasePopover.Positioner
          ref={setPositioner}
          anchor={positionAnchor ?? anchor}
          className="dr-positioner"
          side={side}
          align={align}
          sideOffset={6}
          collisionPadding={8}
          collisionBoundary={
            boundary === 'scroll-parent'
              ? (scrollParent(anchor) ?? 'clipping-ancestors')
              : 'clipping-ancestors'
          }
          data-sheet={compact || undefined}
        >
          <BasePopover.Popup
            ref={popupRef}
            id={popupId}
            className={cx('popover', panel && 'panel', className)}
            style={style}
            aria-label={label}
            data-sheet={compact || undefined}
            initialFocus={focusOnOpen}
            finalFocus={() => {
              if (
                !manageFocus ||
                lastReason.current === 'outside' ||
                lastReason.current === 'focus-out' ||
                !mayReturnFocus()
              )
                return false;
              // Back to what opened it (a filter chip opens the toolbar's filter panel), else the anchor;
              // never away from where focus moved on to (`finalFocus` is false then), nor from what the
              // person pressed while the popup was still animating out (`mayReturnFocus`).
              const opener = layer.finalFocus();
              return opener === true ? anchor : opener;
            }}
            onClick={
              closeOnSelect
                ? (e: MouseEvent<HTMLDivElement>) => {
                    const hit = (e.target as Element).closest(CLOSE_ON_SELECT);
                    if (
                      hit &&
                      e.currentTarget.contains(hit) &&
                      !hit.closest('[aria-disabled="true"],:disabled')
                    )
                      onRequestOpenChange(false, 'select');
                  }
                : undefined
            }
          >
            {compact ? <SheetGrabber onDismiss={() => void layer.requestClose('swipe')} /> : null}
            <LayerParent id={layer.id}>
              {typeof children === 'function' ? children(close) : children}
            </LayerParent>
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
}
